import { useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { useHistory } from 'react-router-dom';

/* UI Elements */
import { Box, Typography, Divider, IconButton, Chip } from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

/* i18n */
import { useIntl } from 'react-intl';

import { parseMMYYYY } from './ExperienceWalletForm';

// Parse a backend date (ISO string, MM/YYYY string, or Date) to a local-time
// Date object so date-fns formats the same month the backend stored.
const toLocalDate = (value) => (typeof value === 'string' ? parseMMYYYY(value) : value);

export default function ExperienceWalletDisplayItem({ experience, setCurrentContent, deleteItem, currentIndex }) {
  const intl = useIntl();
  const history = useHistory();
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;
  const [expandedSkills, setExpandedSkills] = useState(false);
  const startDateValue = toLocalDate(experience.start_date);
  const endDateValue = toLocalDate(experience.end_date);
  const formattedStartDate = startDateValue
    ? format(startDateValue, experience.end_date ? 'MM-yyyy' : 'MM-dd-yyyy')
    : '';
  const formattedEndDate = endDateValue ? format(endDateValue, 'MM-yyyy') : '';

  return (
    <>
      <Box
        sx={{
          // border: (theme) => `dotted 1px ${theme.palette.grey[800]}`,
          backgroundColor: (theme) => theme.palette.grey[200],
          boxShadow: '1px 2px 7px 1px rgba(0, 0, 0, 0.2)',
          borderRadius: 2,
          px: 2,
          py: 2,
          mb: 2,
          mt: 1.5,
        }}
      >
        <Typography sx={{ fontWeight: 700, pr: 1 }}>{experience.title}</Typography>
        {experience.employer_provider ? (
          <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 400 }}>
            {experience.employer_provider}
          </Typography>
        ) : null}
        <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 400 }}>
          {formattedStartDate} -{' '}
          {formattedEndDate ? formattedEndDate : intl.formatMessage({ id: 'experience.present' })}
        </Typography>
      </Box>
      {experience.field?.title ? (
        <Box sx={{ display: 'flex', mb: 1 }}>
          <Typography sx={{ fontWeight: 700, pr: 1 }}>
            {intl.formatMessage({ id: 'experience.drawer.field' })}:
          </Typography>
          <Typography>{experience.field.title}</Typography>
        </Box>
      ) : null}
      <Box sx={{ display: 'flex' }}>
        <Typography sx={{ fontWeight: 700 }}>{intl.formatMessage({ id: 'experience.drawer.skillPlural' })}:</Typography>
        <Box
          sx={{
            px: 1,
            '& .MuiChip-root': {
              mb: 1,
              '&:not(:last-of-type)': {
                mr: 1,
              },
            },
          }}
        >
          {experience.skills && experience.skills.length
            ? (expandedSkills ? experience.skills : experience.skills.slice(0, 6)).map((skill, skillIdx) => (
                <Chip
                  label={skill.name}
                  icon={skill.wallet ? <AccountBalanceWalletIcon /> : null}
                  color={skill.wallet ? 'primary' : 'secondary'}
                  key={skill.id || `skill-${skillIdx}`}
                  size="small"
                  sx={{ '& .MuiChip-icon': { fontSize: 14 }, maxWidth: 210 }}
                  onDelete={skill.wallet ? null : () => setCurrentContent('edit')}
                />
              ))
            : null}
          {experience.skills?.length > 6 ? (
            <Typography
              onClick={() => setExpandedSkills(!expandedSkills)}
              component="span"
              sx={{
                pl: 1,
                textDecoration: 'underline',
                color: (theme) => theme.palette.primary.dark,
                fontSize: 13,
              }}
            >
              {intl.formatMessage({ id: `experience.form.${expandedSkills ? 'seeLess' : 'seeMore'}` })}
            </Typography>
          ) : null}
        </Box>
      </Box>
      <Divider sx={{ my: 1 }} />
      {experience.isJst && experience.sourceType !== 'military' ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, gap: 1 }}>
          <IconButton aria-label="edit" onClick={() => history.push(`${urlPrefix}/jst`)}>
            <EditIcon />
          </IconButton>
        </Box>
      ) : experience.wallet ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, gap: 1 }}>
          <IconButton aria-label="delete" onClick={() => deleteItem(currentIndex)} sx={{ color: 'error.main' }}>
            <DeleteIcon />
          </IconButton>
          <IconButton aria-label="edit" onClick={() => setCurrentContent('edit')}>
            <EditIcon />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5, gap: 1 }}>
          <IconButton aria-label="delete" onClick={() => deleteItem(currentIndex)} sx={{ color: 'error.main' }}>
            <DeleteIcon />
          </IconButton>
          <IconButton aria-label="edit" onClick={() => setCurrentContent('edit')}>
            <EditIcon />
          </IconButton>
        </Box>
      )}
    </>
  );
}
ExperienceWalletDisplayItem.propTypes = {
  setCurrentContent: PropTypes.func,
  deleteItem: PropTypes.func,
  currentIndex: PropTypes.number,
  experience: PropTypes.shape({
    field: PropTypes.object,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
    typeLabel: PropTypes.string,
    title: PropTypes.string,
    employer_provider: PropTypes.string,
    skills: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        slice: PropTypes.func,
      })
    ),
  }),
};
